// Admin → Structure databases: the DFT module's searches of the COD, OPTIMADE providers and the Materials Project. The
// site's Materials Project API key is set here (checked with the Materials Project, stored encrypted, never shown
// again - only its last characters); with it every DFT project can search the Materials Project. Which databases and
// OPTIMADE providers are offered is set in the server's environment (PRAYOG_STRUCTURE_DATABASES,
// PRAYOG_OPTIMADE_PROVIDERS).
import { Badge, Button, Card, Code, Group, PasswordInput, Stack, Table, Text } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ErrorBox, notifyError, notifyOk } from "../../components/Errors";
import { del, formatDate, get, post, put } from "../../lib/api";

export interface ServicesState {
  databases: { id: string; name: string; enabled: boolean }[];
  cod_url: string; mp_api_url: string;
  optimade_providers: { name: string; url: string }[];
  materials_project_key: { stored: boolean; hint?: string; readable?: boolean; updated_at?: string | null; updated_by?: string | null };
  project_keys: number; cache_hours: number; searches_per_minute: number;
}

export function ServicesTab() {
  const qc = useQueryClient();
  const state = useQuery({ queryKey: ["admin-services"], queryFn: () => get<ServicesState>("/admin/services") });
  const [key, setKey] = useState("");
  const done = (s: ServicesState) => qc.setQueryData(["admin-services"], s);
  const save = useMutation({ mutationFn: () => put<ServicesState>("/admin/services/materials_project", { key: key.trim() }),
    onSuccess: (s) => { setKey(""); done(s); notifyOk("The Materials Project key is checked and saved."); } });
  const test = useMutation({ mutationFn: () => post("/admin/services/materials_project/test"),
    onSuccess: () => notifyOk("The Materials Project accepts the stored key."), onError: notifyError });
  const remove = useMutation({ mutationFn: () => del<ServicesState>("/admin/services/materials_project"),
    onSuccess: (s) => { done(s); notifyOk("The Materials Project key is removed."); }, onError: notifyError });
  const s = state.data;
  const mpOn = !!s?.databases.find((d) => d.id === "mp")?.enabled;
  const k = s?.materials_project_key;
  return (
    <Stack gap="sm" data-testid="admin-services">
      <Text size="sm" c="dimmed" maw={900}>
        The DFT module can take a structure from a database: the server searches it and keeps the answers for{" "}
        {s?.cache_hours ?? 24} hours; each user may reach the databases {s?.searches_per_minute ?? 20} times a minute.
      </Text>
      <ErrorBox error={state.error} />
      {s && (
        <>
          <Card withBorder>
            <Text fw={600} mb={4}>Materials Project</Text>
            {!mpOn && <Text size="sm" c="orange">Not offered on this server (PRAYOG_STRUCTURE_DATABASES).</Text>}
            <Text size="sm">
              {k?.stored
                ? <>Site key <Code>{k.hint}</Code>{k.readable === false && <Badge color="red" ml={6} size="sm">cannot be read</Badge>} · set{" "}
                  {formatDate(k.updated_at, true)}{k.updated_by ? ` by ${k.updated_by}` : ""}. Every DFT project can search the
                  Materials Project.</>
                : "No site key: users can search the Materials Project only with their own key, per project."}
            </Text>
            <Text size="xs" c="dimmed" mt={2}>{s.project_keys} project{s.project_keys === 1 ? " has" : "s have"} a user's own key.</Text>
            <form onSubmit={(e) => { e.preventDefault(); if (key.trim()) save.mutate(); }}>
              <Group gap="xs" mt="xs" align="flex-end">
                <PasswordInput size="xs" w={300} label={k?.stored ? "Replace the key" : "API key"} value={key} autoComplete="off"
                  onChange={(e) => setKey(e.currentTarget.value)} placeholder="from the Materials Project dashboard" />
                <Button size="xs" type="submit" loading={save.isPending} disabled={!key.trim()}>Check and save</Button>
                {k?.stored && <Button size="xs" variant="default" loading={test.isPending} onClick={() => test.mutate()}>Test the key</Button>}
                {k?.stored && <Button size="xs" variant="subtle" color="red" loading={remove.isPending}
                  onClick={() => remove.mutate()}>Remove</Button>}
              </Group>
            </form>
            <ErrorBox error={save.error} />
            <Text size="xs" c="dimmed" mt={4}>The key is stored encrypted with the server's secret key (PRAYOG_SECRET_KEY): if that
              changes, enter the key again. API: <Code>{s.mp_api_url}</Code></Text>
          </Card>
          <Card withBorder>
            <Text fw={600} mb={4}>Databases offered</Text>
            <Group gap="xs" mb="xs">
              {s.databases.map((d) => <Badge key={d.id} variant="light" color={d.enabled ? "teal" : "gray"}>{d.name}{d.enabled ? "" : " (off)"}</Badge>)}
            </Group>
            <Text size="xs">COD: <Code>{s.cod_url}</Code></Text>
            <Table fz="xs" mt="xs" withRowBorders={false}>
              <Table.Thead><Table.Tr><Table.Th>OPTIMADE provider</Table.Th><Table.Th>Address</Table.Th></Table.Tr></Table.Thead>
              <Table.Tbody>
                {s.optimade_providers.map((p) => <Table.Tr key={p.name}><Table.Td>{p.name}</Table.Td><Table.Td><Code>{p.url}</Code></Table.Td></Table.Tr>)}
              </Table.Tbody>
            </Table>
            <Text size="xs" c="dimmed" mt={4}>Set in the server's environment: PRAYOG_STRUCTURE_DATABASES (e.g. ["cod","optimade","mp"]) and
              PRAYOG_OPTIMADE_PROVIDERS (JSON: name → address). The server needs to reach these sites (outbound HTTPS).</Text>
          </Card>
        </>
      )}
    </Stack>
  );
}
